/**
 * Domain types for Requirement Lifecycle Management
 *
 * The backend (Kratos protojson codec) serializes enums as their full proto
 * names — e.g. "REQUIREMENT_THREAD_STATUS_DRAFT". The UI works with the short
 * suffix ("DRAFT") everywhere; conversion happens at the api.ts boundary via
 * the helpers below. int64 ids are JSON strings on the wire and are kept as
 * strings in the UI to avoid precision loss.
 */

// ---------------------------------------------------------------------------
// Thread status
// ---------------------------------------------------------------------------

export type ThreadStatus =
  | 'DRAFT'
  | 'WIP'
  | 'APPROVED'
  | 'DELETED'
  | 'SPLIT'
  | 'MERGED'
  | 'PENDING_SPLIT'
  | 'PENDING_MERGE';

export const THREAD_STATUSES: ThreadStatus[] = [
  'DRAFT',
  'WIP',
  'APPROVED',
  'DELETED',
  'SPLIT',
  'MERGED',
  'PENDING_SPLIT',
  'PENDING_MERGE',
];

const THREAD_STATUS_PREFIX = 'REQUIREMENT_THREAD_STATUS_';

/** Maps a backend thread-status enum name to the short UI status.
 * Returns null for unrecognized values so callers can filter them out
 * rather than silently treating an unknown status as DRAFT (which would
 * incorrectly show edit/delete controls on terminal or future states).
 */
export function threadStatusFromEnum(
  value: string | undefined,
): ThreadStatus | null {
  const short = (value ?? '').replace(THREAD_STATUS_PREFIX, '');
  return (THREAD_STATUSES as string[]).includes(short)
    ? (short as ThreadStatus)
    : null;
}

/** Maps a short UI thread status to the backend enum name. */
export function threadStatusToEnum(status: ThreadStatus): string {
  return `${THREAD_STATUS_PREFIX}${status}`;
}

/** Terminal states: no further edit / delete is possible. */
const TERMINAL_THREAD_STATUSES: ThreadStatus[] = [
  'APPROVED',
  'DELETED',
  'SPLIT',
  'MERGED',
];

export function isTerminalThreadStatus(status: ThreadStatus): boolean {
  return TERMINAL_THREAD_STATUSES.includes(status);
}

/** Editable / deletable states (DRAFT or WIP). */
export function isEditableThreadStatus(status: ThreadStatus): boolean {
  return status === 'DRAFT' || status === 'WIP';
}

/**
 * A source of an open Merge proposal. It stays WIP while the
 * proposal is open and points at the PENDING_MERGE target; the backend refuses
 * to edit, approve, delete, split or merge it until the proposal is accepted
 * (it becomes MERGED) or rejected (the link is cleared).
 */
export function isMergeLockedSource(
  thread: Pick<RequirementThread, 'status' | 'mergedIntoThreadId'>,
): boolean {
  return thread.status === 'WIP' && !!thread.mergedIntoThreadId;
}

/**
 * Whether the thread can be deleted: DRAFT or WIP, and not a locked Merge source.
 */
export function canDeleteThread(
  thread: Pick<RequirementThread, 'status' | 'mergedIntoThreadId'>,
): boolean {
  return isEditableThreadStatus(thread.status) && !isMergeLockedSource(thread);
}

/**
 * Whether the thread can be split or selected for a Merge: WIP, and not already
 * locked in an open Merge proposal.
 */
export function isMergeable(
  thread: Pick<RequirementThread, 'status' | 'mergedIntoThreadId'>,
): boolean {
  return thread.status === 'WIP' && !isMergeLockedSource(thread);
}

/** The threads among `threads` that are selected and can still be merged. */
export function selectedMergeable<
  T extends Pick<
    RequirementThread,
    'threadId' | 'status' | 'mergedIntoThreadId'
  >,
>(threads: T[], selectedIds: Set<string>): T[] {
  return threads.filter(t => selectedIds.has(t.threadId) && isMergeable(t));
}

/**
 * The thread whose Merge review to open from `thread`: the target a source
 * points at through mergedIntoThreadId (also a source of a proposal made
 * before the merge direction changed, which is PENDING_MERGE itself), or the
 * PENDING_MERGE target itself, which has no such pointer.
 */
export function mergeReviewTargetId(
  thread: Pick<
    RequirementThread,
    'threadId' | 'status' | 'mergedIntoThreadId'
  > | null,
): string | undefined {
  if (!thread) return undefined;
  return (
    thread.mergedIntoThreadId ??
    (thread.status === 'PENDING_MERGE' ? thread.threadId : undefined)
  );
}

/**
 * Whether the thread's text and metadata can be edited: DRAFT or WIP unless it
 * is a locked Merge source, and the PENDING_MERGE target of an open Merge so
 * the merged text can be revised during the review.
 */
export function canEditThreadContent(
  thread: Pick<RequirementThread, 'status' | 'mergedIntoThreadId'>,
): boolean {
  if (thread.status === 'PENDING_MERGE') return true;
  return isEditableThreadStatus(thread.status) && !isMergeLockedSource(thread);
}

// ---------------------------------------------------------------------------
// Instance (version) status
// ---------------------------------------------------------------------------

export type InstanceStatus =
  | 'CURRENT'
  | 'PENDING_REVIEW'
  | 'SUPERSEDED'
  | 'REJECTED'
  | 'APPROVED'
  | 'DISCARDED';

export const INSTANCE_STATUSES: InstanceStatus[] = [
  'CURRENT',
  'PENDING_REVIEW',
  'SUPERSEDED',
  'REJECTED',
  'APPROVED',
  'DISCARDED',
];

const VERSION_STATUS_PREFIX = 'REQUIREMENT_VERSION_STATUS_';

/** Maps a backend version-status enum name to the short UI status. */
export function instanceStatusFromEnum(
  value: string | undefined,
): InstanceStatus {
  const short = (value ?? '').replace(VERSION_STATUS_PREFIX, '');
  return (INSTANCE_STATUSES as string[]).includes(short)
    ? (short as InstanceStatus)
    : 'SUPERSEDED';
}

// ---------------------------------------------------------------------------
// Instance source (channel that produced a version)
// ---------------------------------------------------------------------------

export type InstanceSource = 'USER' | 'AGENT' | 'AI' | 'UNKNOWN';

const INSTANCE_SOURCES: InstanceSource[] = ['USER', 'AGENT', 'AI'];

const SOURCE_PREFIX = 'REQUIREMENT_SOURCE_';

/**
 * Maps a backend source enum name to the short UI source. UNSPECIFIED / unknown
 * / missing all collapse to "UNKNOWN" so callers can choose not to render a
 * badge rather than mislabel the channel.
 */
export function instanceSourceFromEnum(
  value: string | undefined,
): InstanceSource {
  const short = (value ?? '').replace(SOURCE_PREFIX, '');
  return (INSTANCE_SOURCES as string[]).includes(short)
    ? (short as InstanceSource)
    : 'UNKNOWN';
}

// ---------------------------------------------------------------------------
// Domain models (UI-facing, normalized from the backend DTOs in api.ts)
// ---------------------------------------------------------------------------

export interface RequirementThread {
  threadId: string;
  projectId: string;
  name: string;
  externalRef: string;
  status: ThreadStatus;
  acceptedVersionId: string; // "0" when none accepted
  createdAt: string;
  updatedAt: string;
  // Lineage undefined when none.
  parentThreadId: string | undefined;
  mergedIntoThreadId: string | undefined;
  // Audit fields "0" / "" when unrecorded.
  createdBy: string;
  submittedAt: string;
  submittedBy: string;
  approvedAt: string;
  approvedBy: string;
  // Descriptive metadata Empty when unset.
  description: string;
  tags: string[];
  owner: string;
  // Text of the CURRENT version. Empty when the thread has no
  // CURRENT version. Prefer this over `name`, which is only the first 80
  // characters of the text — or the external_ref entirely.
  text: string;
  // ID of the CURRENT version `text` belongs to. Undefined when the thread has
  // none. Not interchangeable with acceptedVersionId, which stays "0" until the
  // thread is approved.
  currentVersionId?: string;
  // Scores of the last completed analysis of the CURRENT version. Absent when
  // it was never analysed, which is not the same as a score of 0.
  aspiceScore?: number;
  incoseScore?: number;
  // Category that same analysis assigned ("Unknown" when categorisation
  // failed). Absent when it was never analysed.
  category?: string;
}

export interface RequirementInstance {
  versionId: string;
  threadId: string;
  text: string;
  status: InstanceStatus;
  createdAt: string;
  updatedAt: string;
  // Channel that produced this version "UNKNOWN" when the
  // backend did not report a source.
  source: InstanceSource;
  // 1-based sequence number within the thread's history, or 0 when the backend
  // did not compute it (e.g. on mutation responses)
  versionNumber: number;
  // User id (or system principal) that produced this version; "0" when
  // unrecorded
  createdBy: string;
  // Free-form key/value metadata captured for this version.
  metadata: Record<string, string>;
}

/** Whether to register requirements as DRAFT (default) or directly APPROVED. */
export type ImportMode = 'draft' | 'approved';
